import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning, CheckCircle } from "@phosphor-icons/react";

/**
 * Reset-password screen. Users land here from the email link:
 *   /reset-password?token=<uuid>
 * POST to /api/auth/reset-password with { token, new_password }.
 * On success we redirect to /login so they can sign in with the new password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Reset link is missing its token — request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2)   { toast.error("Passwords don't match"); return; }
    if (pw1.length < 6){ toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password updated — you can sign in now.");
      setTimeout(() => nav("/login", { replace: true }), 1400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed — link may be expired.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid="reset-password-page">
      <div className="flex-1 hidden md:flex items-center justify-center bg-secondary/40 border-r border-border p-16">
        <div className="max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="font-display text-xl font-bold">Tradelog</span>
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight">Choose a new password</h1>
          <p className="mt-4 text-muted-foreground">
            Pick something you'll actually remember this time. Minimum 6 characters.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="rp-form">
          <div>
            <h2 className="font-display text-3xl font-bold">Reset password</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enter and confirm your new password.
            </p>
          </div>

          {done ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-400 flex items-start gap-2" data-testid="rp-done">
              <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
              <span>Password updated. Redirecting you to sign in…</span>
            </div>
          ) : (
            <>
              <div>
                <Label>New password</Label>
                <Input type="password" required minLength={6} value={pw1}
                  onChange={e => setPw1(e.target.value)} data-testid="rp-pw1" autoFocus />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input type="password" required minLength={6} value={pw2}
                  onChange={e => setPw2(e.target.value)} data-testid="rp-pw2" />
              </div>
              <Button type="submit" disabled={loading || !token || !pw1 || !pw2}
                className="w-full rounded-full" data-testid="rp-submit">
                {loading ? "Saving…" : "Update password"}
              </Button>
              {!token && (
                <p className="text-xs text-amber-500" data-testid="rp-no-token">
                  This link is missing its reset token. Please request a fresh email from
                  the <Link to="/forgot-password" className="underline">forgot password</Link> page.
                </p>
              )}
            </>
          )}

          <p className="text-sm text-muted-foreground text-center">
            Remembered it? <Link to="/login" className="text-primary underline" data-testid="rp-back-login">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
